/**
 * Pure @-mention logic shared by the web Composer and the React Native
 * composer: caret detection, candidate ranking, and the text/caret splice when
 * a candidate is picked. No DOM, no React — just string + index math, so both
 * platforms get identical behavior and one test suite covers it.
 *
 * Wire format: a mention is the literal `@username ` in the message content.
 * The server recomputes `Message.mentions` (user ids) from that text, so the
 * client only has to insert the token; the id array it sends is advisory.
 */

/** Minimal shape the ranker needs; each platform's richer candidate satisfies it. */
export interface MentionLike {
  username: string;
  displayName: string | null;
}

/** Active mention token detected before the caret. */
export interface MentionToken {
  /** Index of the `@` in the full value. */
  anchor: number;
  /** Text after the `@` up to the caret (the partial query). */
  query: string;
}

/** Result of splicing a picked mention into a draft. */
export interface MentionInsert {
  /** The rebuilt draft with `@username ` spliced in at the anchor. */
  text: string;
  /** Caret position right after the inserted mention. */
  caret: number;
}

/**
 * Inspect the text BEFORE `caret`. If the most recent token is `@…` with no
 * whitespace inside (and the `@` sits at start-of-string or after whitespace),
 * return its anchor + partial query. Otherwise null (popup should close).
 */
export function detectMentionQuery(value: string, caret: number): MentionToken | null {
  const before = value.slice(0, Math.max(0, caret));
  const at = before.search(/(^|\s)@[\w.-]*$/);
  if (at < 0) return null;
  // Skip the optional leading-whitespace char the regex matched.
  const anchor = before[at] === '@' ? at : at + 1;
  return { anchor, query: before.slice(anchor + 1) };
}

/**
 * Filter + rank mention candidates for a query: substring match on username
 * or display name, prefix matches first, then alphabetical (ru locale).
 * An empty query returns the first `limit` candidates unranked.
 */
export function rankMentions<T extends MentionLike>(candidates: T[], query: string, limit = 8): T[] {
  const q = query.toLowerCase();
  if (!q) return candidates.slice(0, limit);
  return candidates
    .filter((c) => {
      const u = c.username.toLowerCase();
      const d = (c.displayName ?? '').toLowerCase();
      return u.includes(q) || d.includes(q);
    })
    .sort((a, b) => {
      const ua = a.username.toLowerCase();
      const ub = b.username.toLowerCase();
      const ap = ua.startsWith(q) ? 0 : 1;
      const bp = ub.startsWith(q) ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return ua.localeCompare(ub, 'ru');
    })
    .slice(0, limit);
}

/**
 * Splice `@username ` into `draft`, replacing the in-progress `@…` token that
 * spans [anchor, caret). Everything before the anchor and from the caret on is
 * preserved. Returns the new draft and where the caret should land.
 */
export function insertMention(draft: string, anchor: number, caret: number, username: string): MentionInsert {
  const before = draft.slice(0, anchor);
  const after = draft.slice(caret);
  const inserted = `@${username} `;
  return { text: before + inserted + after, caret: before.length + inserted.length };
}

/**
 * Split message content into plain-text and mention runs for rendering. A run
 * is a mention only when its `@username` resolves against `usernames` (a set of
 * known member usernames) — bare `@notamember` stays literal text. Matching is
 * case-insensitive on the token; the returned `username` is the canonical one
 * from the set so the renderer can resolve it to a user.
 */
export interface MentionRun {
  type: 'text' | 'mention';
  value: string;
  /** Canonical username (without `@`) when type === 'mention'. */
  username?: string;
}

export function splitMentions(content: string, usernames: Iterable<string>): MentionRun[] {
  const canonical = new Map<string, string>();
  for (const u of usernames) canonical.set(u.toLowerCase(), u);
  if (canonical.size === 0) return content ? [{ type: 'text', value: content }] : [];

  const runs: MentionRun[] = [];
  const re = /@([\w.-]+)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    const handle = m[1];
    if (!handle) continue;
    const hit = canonical.get(handle.toLowerCase());
    if (!hit) continue; // not a real member — leave as plain text
    if (m.index > last) runs.push({ type: 'text', value: content.slice(last, m.index) });
    runs.push({ type: 'mention', value: m[0], username: hit });
    last = m.index + m[0].length;
  }
  if (last < content.length) runs.push({ type: 'text', value: content.slice(last) });
  return runs;
}
